import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { useEnrollmentProductSelection } from "@dashboard/downloads/hooks/useProductVersionSelection";
import { ReleaseFilesState } from "@dashboard/downloads/hooks/useReleaseFiles";
import { Box, Card, CardContent, Theme, Typography } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { LoadingBox } from "@shared/Loading";
import { PageContentWrapper } from "@shared/PageContentWrapper/PageContentWrapper";

import { EnrollmentAndroidOsStepper } from "./EnrollmentAndroidOsStepper";

const useStyles = makeStyles()((theme: Theme) => ({
    wrapper: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
    },
    container: {
        width: "100%",
        borderRadius: theme.spacing(2),
    },
    content: {
        flex: "0 0 auto",
        padding: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(4),
    },
    titleContainer: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(3),
        color: theme.palette.emteria.text.primary,
        textAlign: "left",
    },
    description: {
        color: theme.palette.emteria.text.secondary,
        marginBottom: theme.spacing(2),
    },
    stepper: {
        marginBottom: theme.spacing(1),
    },
    selectContainer: {
        marginTop: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
    },
    actions: {
        padding: theme.spacing(3, 4),
        display: "flex",
        gap: theme.spacing(2),
        justifyContent: "space-between",
    },
    actionsLeft: {
        display: "flex",
        gap: theme.spacing(2),
    },
}));

interface SelectDeviceTypeStepProps {
    onSkip: () => void;
    onBack?: () => void;
    onContinue: (releaseFile: API.ReleaseFile) => void;
    releaseFiles: ReleaseFilesState;
}

export const SelectDeviceTypeStep: React.FC<SelectDeviceTypeStepProps> = ({ onSkip, onBack, onContinue, releaseFiles }) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);
    const {
        selectedProductCodename,
        setSelectedProductCodename,
        selectedVersion,
        setSelectedVersion,
        productOptions,
        versionOptions,
        selectedFile,
    } = useEnrollmentProductSelection({ releaseFiles });

    const handleContinue = () => {
        if (selectedFile) {
            onContinue(selectedFile);
        }
    };

    if (releaseFiles.status === "pending") {
        return (
            <PageContentWrapper size="wide" className={classes.wrapper}>
                <EnrollmentAndroidOsStepper activeStep={0} className={classes.stepper} />
                <Card className={classes.container} elevation={3}>
                    <CardContent className={classes.content}>
                        <LoadingBox style={{ minHeight: 300 }} />
                    </CardContent>
                </Card>
            </PageContentWrapper>
        );
    }

    return (
        <PageContentWrapper size="wide" className={classes.wrapper}>
            <EnrollmentAndroidOsStepper activeStep={0} className={classes.stepper} />
            <Card className={classes.container} elevation={3}>
                <CardContent className={classes.content}>
                    <Box className={classes.titleContainer}>
                        <Typography variant="h6" className={classes.title}>
                            {t("modals:enrollmentDialog.imageSelectionStep.title")}
                        </Typography>
                    </Box>
                    <Typography variant="body1" className={classes.description}>
                        {t("modals:enrollmentDialog.imageSelectionStep.description")}
                    </Typography>
                    {productOptions.length === 0 ? (
                        <CustomAlert severity="info" style={{ marginTop: 16 }}>
                            {t("modals:enrollmentDialog.imageSelectionStep.noProductsAvailable")}
                        </CustomAlert>
                    ) : (
                        <Box className={classes.selectContainer}>
                            <SelectField
                                label={t("modals:enrollmentDialog.imageSelectionStep.imageLabel")}
                                options={productOptions}
                                value={productOptions.find((option) => option.value === selectedProductCodename)}
                                onChange={(_event, selectedOption) => {
                                    if (selectedOption && typeof selectedOption.value === "string") {
                                        setSelectedProductCodename(selectedOption.value);
                                    }
                                }}
                                fullWidth
                                disableSorting
                            />
                            {versionOptions.length === 0 && selectedProductCodename ? (
                                <CustomAlert severity="info">
                                    {t("modals:enrollmentDialog.imageSelectionStep.noVersionsAvailable")}
                                </CustomAlert>
                            ) : (
                                <SelectField
                                    label={t("modals:enrollmentDialog.imageSelectionStep.androidVersionLabel")}
                                    options={versionOptions}
                                    value={versionOptions.find((option) => option.value === selectedVersion)}
                                    onChange={(_event, selectedOption) => {
                                        if (selectedOption && typeof selectedOption.value === "string") {
                                            setSelectedVersion(selectedOption.value);
                                        }
                                    }}
                                    fullWidth
                                    disableSorting
                                    disabled={!selectedProductCodename || versionOptions.length === 0}
                                />
                            )}
                        </Box>
                    )}
                </CardContent>
                <Box className={classes.actions}>
                    <Box className={classes.actionsLeft}>
                        <SecondaryButton onClick={onBack || onSkip}>{t("common:button.goBack")}</SecondaryButton>
                    </Box>
                    <PrimaryButton onClick={handleContinue} disabled={!selectedProductCodename || !selectedVersion}>
                        {t("common:button.continue")}
                    </PrimaryButton>
                </Box>
            </Card>
        </PageContentWrapper>
    );
};
